import { useSyncExternalStore } from 'react'

export type TenantAccountViewFocus = {
  accountId: string
  accountNumber: string
  accountName: string
  open: boolean
  tabIndex: number
  tabTitle: string
}

export type TenantViewFocus = {
  tenantId: string
  accounts: TenantAccountViewFocus[]
}

/** Base titles when Conductor is hidden (no `conductor:read`). */
export const TENANT_ACCOUNT_TAB_TITLES = [
  'General',
  'Mailing preference',
  'Service agreements',
  'Account managers',
  'Payments',
  'Invoices',
  'Billing events',
  'Billing options',
  'Incentives',
  'Other Documents',
] as const

/**
 * Tab titles in Tenant.tsx TabPanel child order (index matches selectedKey / ?tab=).
 * Conductor is appended last so indices 0–9 stay stable when `conductor:read` is present.
 */
export function tenantAccountTabTitles(includeConductor: boolean): string[] {
  const titles: string[] = [...TENANT_ACCOUNT_TAB_TITLES]
  if (includeConductor) {
    titles.push('Conductor')
  }
  return titles
}

export function conductorTabIndex(includeConductor: boolean): number | null {
  if (!includeConductor) {
    return null
  }
  return tenantAccountTabTitles(true).indexOf('Conductor')
}

let currentFocus: TenantViewFocus | null = null
const listeners = new Set<() => void>()

function emit(): void {
  listeners.forEach((listener) => listener())
}

/** Called from Tenant page when expanded panels / tabs change. Cleared on unmount. */
export function setTenantViewFocus(focus: TenantViewFocus | null): void {
  currentFocus = focus
  emit()
}

export function getTenantViewFocus(): TenantViewFocus | null {
  return currentFocus
}

export function subscribeTenantViewFocus(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function useTenantViewFocus(): TenantViewFocus | null {
  return useSyncExternalStore(subscribeTenantViewFocus, getTenantViewFocus, () => null)
}

export function tabTitleForIndex(index: number, includeConductor = false): string {
  return tenantAccountTabTitles(includeConductor)[index] ?? `Tab ${index}`
}
